import { useEffect, useState } from 'react';
import {
    Box, Typography, Button, Stack, Alert, CircularProgress, Card, CardContent,
    IconButton, Tooltip, Chip, Pagination,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';
import { ApiImageThumb } from '../components/ApiImage';


/**
 * Moderācijas rinda foto pielikumiem uz ārējām atsauksmēm.
 *
 * Server endpointi:
 *   POST /api/v1/admin/moderation/external-review-photos/pending
 *   POST /api/v1/admin/moderation/external-review-photos/decide
 *   POST /api/v1/admin/moderation/external-review-photos/delete
 *
 * Dzēšana atšķiras no noraidīšanas: foto tiek soft-delete'ots images tabulā un pazūd
 * pilnībā (arī autoram), un autoram push paziņojums NEtiek sūtīts.
 */
type PendingItem = {
    imageId: string;
    source: string;
    externalId: string;
    url: string;
    shortDescription: string | null;
    reviewUserId: string;
    reviewDescription: string | null;
    placeAddress: string | null;
    uploadedAt: string;
};

type PendingResponse = {
    isSuccess: boolean;
    error: string | null;
    totalPending: number;
    items: PendingItem[];
};


const PAGE_SIZE = 20;


export function ExternalReviewPhotosModerationPage() {
    const queryClient = useQueryClient();
    const [page, setPage] = useState(1);
    const [busyKey, setBusyKey] = useState<string | null>(null);
    const [confirmDelete, setConfirmDelete] = useState<PendingItem | null>(null);

    const skip = (page - 1) * PAGE_SIZE;

    const { data, isLoading, error, refetch, isFetching } = useQuery({
        queryKey: ['admin-external-review-photo-moderation', skip],
        queryFn: async () => {
            const resp = await api.post<PendingResponse>(
                '/api/v1/admin/moderation/external-review-photos/pending',
                { skip, take: PAGE_SIZE },
            );
            return resp.data;
        },
        staleTime: 30_000,
    });

    const decideMutation = useMutation({
        mutationFn: async (args: { source: string; externalId: string; imageId: string; accepted: boolean }) => {
            const resp = await api.post<{ isSuccess: boolean; error: string | null }>(
                '/api/v1/admin/moderation/external-review-photos/decide',
                args,
            );
            if (!resp.data.isSuccess) throw new Error(resp.data.error || 'Lēmums neizdevās.');
            return args;
        },
        onMutate: (args) => setBusyKey(`${args.source}:${args.externalId}:${args.imageId}`),
        onSettled: () => setBusyKey(null),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-external-review-photo-moderation'] });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (args: { source: string; externalId: string; imageId: string }) => {
            const resp = await api.post<{ isSuccess: boolean; error: string | null }>(
                '/api/v1/admin/moderation/external-review-photos/delete',
                args,
            );
            if (!resp.data.isSuccess) throw new Error(resp.data.error || 'Dzēšana neizdevās.');
            return args;
        },
        onMutate: (args) => setBusyKey(`${args.source}:${args.externalId}:${args.imageId}`),
        onSettled: () => setBusyKey(null),
        onSuccess: () => {
            setConfirmDelete(null);
            queryClient.invalidateQueries({ queryKey: ['admin-external-review-photo-moderation'] });
        },
    });

    const mutationError = (decideMutation.error ?? deleteMutation.error) as Error | null;

    const items = data?.items ?? [];
    const totalPending = data?.totalPending ?? 0;
    const pageCount = Math.max(1, Math.ceil(totalPending / PAGE_SIZE));

    useEffect(() => {
        if (!isLoading && items.length === 0 && page > 1) {
            setPage((p) => Math.max(1, p - 1));
        }
    }, [items.length, isLoading, page]);

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Box>
                    <Typography variant="h4">Unverified foto moderācija</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Foto pielikumi uz ārējām atsauksmēm (Google, OpenStreetMap, Refugee, Foursquare).
                        Apstiprinātie foto kļūst publiski; noraidītie paliek paslēpti.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Chip
                        label={`Kopā gaida: ${totalPending}`}
                        color={totalPending > 0 ? 'warning' : 'default'}
                    />
                    <Tooltip title="Atjaunot">
                        <span>
                            <IconButton onClick={() => refetch()} disabled={isFetching}>
                                <RefreshIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    Neizdevās ielādēt moderācijas rindu: {String((error as Error).message)}
                </Alert>
            )}

            {mutationError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    Darbība neizdevās: {mutationError.message}
                </Alert>
            )}

            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            )}

            {!isLoading && items.length === 0 && (
                <Alert severity="success">
                    Šobrīd nav nekā, ko moderēt. Visi ārējie foto ir apstrādāti.
                </Alert>
            )}

            <Stack spacing={1.5}>
                {items.map((it) => {
                    const key = `${it.source}:${it.externalId}:${it.imageId}`;
                    return (
                        <Card key={key}>
                            <CardContent>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }}>
                                    <ApiImageThumb
                                        url={it.url}
                                        alt={it.shortDescription ?? 'external review photo'}
                                    />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                                            <Chip label={it.source} size="small" variant="outlined" />
                                            <Typography variant="caption" color="text.secondary">
                                                par {it.placeAddress ?? '(nezināma adrese)'}
                                            </Typography>
                                        </Stack>
                                        {it.reviewDescription && (
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1, fontStyle: 'italic' }}>
                                                „{it.reviewDescription}"
                                            </Typography>
                                        )}
                                        <Stack direction="row" spacing={2}>
                                            <Typography variant="caption" color="text.secondary">
                                                {new Date(it.uploadedAt).toLocaleString('lv-LV')}
                                            </Typography>
                                            <Tooltip title={it.reviewUserId}>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ fontFamily: 'monospace' }}
                                                    noWrap
                                                >
                                                    Autors: {it.reviewUserId.slice(0, 8)}…
                                                </Typography>
                                            </Tooltip>
                                            <Tooltip title={it.externalId}>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ fontFamily: 'monospace' }}
                                                    noWrap
                                                >
                                                    ID: {it.externalId.slice(0, 12)}…
                                                </Typography>
                                            </Tooltip>
                                        </Stack>
                                    </Box>
                                    <Stack direction="row" spacing={1}>
                                        <Button
                                            color="success"
                                            variant="contained"
                                            startIcon={<CheckCircleOutlineIcon />}
                                            disabled={busyKey === key}
                                            onClick={() => decideMutation.mutate({
                                                source: it.source,
                                                externalId: it.externalId,
                                                imageId: it.imageId,
                                                accepted: true,
                                            })}
                                        >
                                            Apstiprināt
                                        </Button>
                                        <Button
                                            color="error"
                                            variant="outlined"
                                            startIcon={<HighlightOffIcon />}
                                            disabled={busyKey === key}
                                            onClick={() => decideMutation.mutate({
                                                source: it.source,
                                                externalId: it.externalId,
                                                imageId: it.imageId,
                                                accepted: false,
                                            })}
                                        >
                                            Noraidīt
                                        </Button>
                                        <Tooltip title="Dzēst pavisam (bez paziņojuma autoram)">
                                            <span>
                                                <IconButton
                                                    color="error"
                                                    disabled={busyKey === key}
                                                    onClick={() => setConfirmDelete(it)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>
                    );
                })}
            </Stack>

            {pageCount > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                        page={page}
                        count={pageCount}
                        onChange={(_, p) => setPage(p)}
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}

            {confirmDelete && (
                <Dialog open onClose={() => setConfirmDelete(null)}>
                    <DialogTitle>Dzēst foto?</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Foto tiks dzēsts pavisam — tas pazudīs arī autoram, un autors
                            paziņojumu nesaņems. Ja gribi, lai autors uzzina, izmanto „Noraidīt".
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmDelete(null)}>Atcelt</Button>
                        <Button
                            color="error"
                            disabled={deleteMutation.isPending}
                            onClick={() => deleteMutation.mutate({
                                source: confirmDelete.source,
                                externalId: confirmDelete.externalId,
                                imageId: confirmDelete.imageId,
                            })}
                        >
                            Dzēst
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Box>
    );
}
